import React from 'react';
import AddNewItemButton from '../../../../Home/EditableScene/AddNewItemButton';
import { Controller, useFormContext } from 'react-hook-form';
import { Collapse, Icon, Input, Progress, Tooltip, Upload } from 'antd';
import DragHandle from 'components/miscellaneous/DragHandle';
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import { v4 } from 'uuid';
import { useCreateEmailExclusionContext } from '../context';
import WholeDirectoryUploader from './WholeDirectoryUploader';
import PostExclusionCriteriaInput from './PostExclusionCriteriaInput';

const { Dragger } = Upload;

const SortableList = SortableContainer(({ children }) => {
  return <div className="space-y-2">{children}</div>;
});

const SortableItem = SortableElement(({ children }) => {
  return <>{children}</>;
});

export default function CampaignFilesUploader() {
  const form = useFormContext();
  const { isUploading } = useCreateEmailExclusionContext();
  const [sorting, setSorting] = React.useState(false);
  return (
    <div className="space-y-2">
      <b>Campaign Files</b>
      {!isUploading && <WholeDirectoryUploader />}
      <Controller
        control={form.control}
        name="campaignFiles"
        render={({ field }) => {
          return (
            <>
              <SortableList
                useDragHandle
                onSortEnd={({ oldIndex, newIndex }) => {
                  setSorting(false);
                  field.onChange(arrayMove(field.value, oldIndex, newIndex));
                }}
                onSortStart={() => setSorting(true)}
              >
                {field.value.map((folder, index) => (
                  <SortableItem index={index} key={folder.id}>
                    <RenderFolderUpload
                      folder={folder}
                      index={index}
                      isDuplicateFolderName={
                        field.value.filter(f => f.folder === folder.folder)
                          .length > 1
                      }
                      isFirst={index === 0}
                      isLast={index === field.value.length - 1}
                      key={folder.id}
                      onDelete={() => {
                        const newFolders = [...field.value];
                        newFolders.splice(index, 1);
                        field.onChange(newFolders);
                      }}
                      onDown={() => {
                        field.onChange(
                          arrayMove(field.value, index, index + 1),
                        );
                      }}
                      onFolderChange={updated => {
                        const newFolders = [...field.value];
                        newFolders[index] = updated;
                        field.onChange(newFolders);
                      }}
                      onUp={() => {
                        field.onChange(
                          arrayMove(field.value, index, index - 1),
                        );
                      }}
                    />
                  </SortableItem>
                ))}
              </SortableList>
              {!sorting && !isUploading && (
                <AddNewItemButton
                  text="Add New Folder"
                  onClick={() => {
                    field.onChange([
                      ...field.value,
                      {
                        id: v4(),
                        folder: 'New Folder',
                        files: [],
                        postExclusionCriteria: {
                          domains: [],
                          providers: [],
                          ranks: [],
                          companyNameRegexp: '',
                        },
                      },
                    ]);
                  }}
                />
              )}
            </>
          );
        }}
      />
    </div>
  );
}
function RenderFolderUpload({
  onDelete,
  onFolderChange,
  folder,
  index,
  isDuplicateFolderName,
  isLast,
  isFirst,
  onUp,
  onDown,
}) {
  const { progress, isUploading } = useCreateEmailExclusionContext();
  return (
    <Collapse accordion activeKey={1}>
      <Collapse.Panel
        header={
          <div
            className="flex w-full items-center"
            style={{ justifyContent: 'space-between' }}
          >
            <div className="flex items-center w-full space-x-2">
              <Input
                className={isDuplicateFolderName ? 'error' : ''}
                disabled={isUploading}
                placeholder="Group Name"
                style={{ maxWidth: 200 }}
                value={folder.folder}
                onChange={({ target: { value } }) => {
                  onFolderChange({
                    ...folder,
                    folder: value,
                  });
                }}
                onClick={e => e.stopPropagation()}
              />
              {isDuplicateFolderName && (
                <div className="error-message">Folder name must be unique</div>
              )}
            </div>
            {!isUploading && (
              <div className="flex items-center space-x-2">
                {!isFirst && (
                  <Tooltip title="Move up">
                    <Icon
                      style={{ fontSize: 16 }}
                      type="arrow-up"
                      onClick={e => {
                        e.stopPropagation();
                        onUp();
                      }}
                    />
                  </Tooltip>
                )}
                {!isLast && (
                  <Tooltip title="Move down">
                    <Icon
                      style={{ fontSize: 16 }}
                      type="arrow-down"
                      onClick={e => {
                        e.stopPropagation();
                        onDown();
                      }}
                    />
                  </Tooltip>
                )}
                <Icon
                  style={{ fontSize: 16 }}
                  theme="twoTone"
                  twoToneColor="#ff3333"
                  type="delete"
                  onClick={e => {
                    e.stopPropagation();
                    onDelete();
                  }}
                />
                <DragHandle />
              </div>
            )}
          </div>
        }
        key={1}
      >
        {!isUploading && (
          <Dragger
            accept="text/csv"
            beforeUpload={(file, fileList) => {
              onFolderChange({
                ...folder,
                files: fileList,
              });
              return false;
            }}
            multiple
            showUploadList={false}
          >
            <Icon style={{ fontSize: 30 }} type={'upload'} />
            <p className="ant-upload-text">
              Click or drag file to this area to upload campaign files
            </p>
          </Dragger>
        )}
        <div className="w-full space-y-2" style={{ marginTop: 10 }}>
          {folder.files.map(file => {
            return (
              <div
                className="flex items-center justify-between w-full"
                key={file.uid}
                style={{ maxWidth: '100%' }}
              >
                <div className="space-x-2 truncate" style={{ flex: 1 }}>
                  <Icon style={{ marginRight: 5 }} type="paper-clip" />
                  {file.name}
                </div>
                {isUploading ? (
                  <Progress
                    format={percent =>
                      `${(percent || 0).toFixed(1).replace(/\.0$/, '')}%`
                    }
                    percent={
                      progress[`campaignFiles:${folder.folder}:${file.name}`]
                    }
                    style={{ maxWidth: 150, marginLeft: 10 }}
                  />
                ) : (
                  <Icon
                    className="cursor-pointer"
                    style={{ fontSize: 20, marginLeft: 10 }}
                    type="delete"
                    onClick={() => {
                      onFolderChange({
                        ...folder,
                        files: folder.files.filter(f => f.uid !== file.uid),
                      });
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="w-full space-y-2" style={{ marginTop: 10 }}>
          <div className="space-x-2 items-center">
            <b>Post Exclusion Criteria</b>
            <Tooltip title="If specified, selected values will be EXCLUDED from output file">
              <Icon
                style={{ fontSize: 16, color: '#1890ff' }}
                type="info-circle"
              />
            </Tooltip>
          </div>
          <PostExclusionCriteriaInput
            fieldPath={`campaignFiles.${index}.postExclusionCriteria`}
          />
        </div>
      </Collapse.Panel>
    </Collapse>
  );
}
